/**
 * useHomeENDemoComposeShellLogic
 *
 * Pure logic hook — state machine, orbital bridge, actions.
 * Use the companion component for rendering.
 *
 * @generated from schema
 * @trait HomeENDemoComposeShell
 */

import { useReducer, useCallback, useEffect, useRef, useMemo } from 'react';
import { useOrbitalBridge } from '@/hooks/useOrbitalBridge';
import type { EntityRow, EventPayload, EventPayloadValue, FieldValue } from '@almadar/core';
function getNestedValue(obj: EventPayload | EventPayloadValue | FieldValue | Record<string, FieldValue | undefined> | null | undefined, path: string): FieldValue | undefined {
  if (obj === null || obj === undefined || !path) return undefined;
  if (typeof obj !== 'object') return undefined;
  if (!path.includes('.') && !Array.isArray(obj)) {
    return (obj as Record<string, FieldValue | undefined>)[path];
  }
  let value: EventPayload | EventPayloadValue | FieldValue | Record<string, FieldValue | undefined> | null | undefined = obj;
  for (const part of path.split('.')) {
    if (value === null || value === undefined || typeof value !== 'object') return undefined;
    if (Array.isArray(value)) {
      if (!/^\d+$/.test(part)) return undefined;
      value = (value as readonly (EventPayloadValue | FieldValue | null | undefined)[])[Number(part)];
      continue;
    }
    value = (value as Record<string, FieldValue | undefined>)[part];
  }
  return value as FieldValue | undefined;
}
import type { EventResponse, HomeENDemoComposeTask, HomeENDemoComposeShellEventPayloadMap } from '@app/shared';
import { useUIEvents } from '@almadar/ui/hooks';
import { registerTraitSnapshot, type TraitStateSnapshot } from '@almadar/ui/lib';
import { createCommandSendPump } from '@almadar/ui/lib';
import { useTranslate } from '@almadar/ui/hooks';

// ============================================================================
// State Machine
// ============================================================================

export type HomeENDemoComposeShellState =
  | 'composing';

export type HomeENDemoComposeShellEvent =
  | 'INIT'
  | 'AppLayoutStateLoaded'
  | 'AppLayoutStateLoadFailed'
  | 'SEARCH'
  | 'NOTIFY_CLICK';

type HomeENDemoComposeShellSlot = 'main' | 'toast';
interface HomeENDemoComposeShellInternalState {
  machineState: HomeENDemoComposeShellState;
  data: Record<string, EntityRow[]>;
  lastPayload: EventPayload | undefined;
  lastEvent: HomeENDemoComposeShellEvent | null;
  slotWriter: Partial<Record<HomeENDemoComposeShellSlot, HomeENDemoComposeShellEvent | null>>;
  loading: boolean;
  pendingEvent: HomeENDemoComposeShellEvent | null;
  pendingFrom: string | null;
  error: string | null;
  fields: Partial<HomeENDemoComposeTask>;
}

type HomeENDemoComposeShellAction =
  | { type: 'EVENT_SUCCESS'; event: HomeENDemoComposeShellEvent; newState: string; data: Record<string, EntityRow[]>; payload?: EventPayload; fields?: Record<string, EventPayloadValue | undefined>; renderBearing?: boolean; renderWrite?: HomeENDemoComposeShellSlot[]; renderClear?: HomeENDemoComposeShellSlot[] }
  | { type: 'EVENT_ERROR'; error: string }
  | { type: 'RESET_ERROR' }
  | { type: 'START_EVENT'; event: HomeENDemoComposeShellEvent; from: string }
  | { type: 'END_EVENT' }
  | { type: 'SET_FIELD'; field: keyof HomeENDemoComposeTask & string; value: EventPayloadValue | undefined }
  | { type: 'TICK'; updates: Record<string, EventPayloadValue | undefined>; collection: string };

function homeENDemoComposeShellReducer(
  state: HomeENDemoComposeShellInternalState,
  action: HomeENDemoComposeShellAction
): HomeENDemoComposeShellInternalState {
  switch (action.type) {
    case 'START_EVENT':
      return { ...state, loading: true, pendingEvent: action.event, pendingFrom: action.from };
    case 'END_EVENT':
      return { ...state, loading: false, pendingEvent: null, pendingFrom: null };
    case 'EVENT_SUCCESS':
      return {
        machineState: action.newState as HomeENDemoComposeShellState,
        data: { ...state.data, ...action.data },
        lastPayload: action.renderBearing ? (action.payload ?? state.lastPayload) : state.lastPayload,
        lastEvent: action.renderBearing ? action.event : state.lastEvent,
        slotWriter: {
          ...state.slotWriter,
          ...Object.fromEntries((action.renderWrite ?? []).map((s) => [s, action.event] as const)),
          ...Object.fromEntries((action.renderClear ?? []).map((s) => [s, null] as const)),
        },
        loading: false,
        pendingEvent: null,
        pendingFrom: null,
        error: null,
        fields: { ...state.fields, ...(action.fields ?? {}) } as Partial<HomeENDemoComposeTask>,
      };
    case 'EVENT_ERROR':
      return { ...state, loading: false, pendingEvent: null, pendingFrom: null, error: action.error };
    case 'RESET_ERROR':
      return { ...state, error: null };
    case 'SET_FIELD': {
      // Back-compat for non-codegen consumers that still dispatch
      // SET_FIELD directly. Codegen folds field updates into
      // EVENT_SUCCESS so transitions commit in a single render.
      return {
        ...state,
        fields: { ...state.fields, [action.field]: action.value } as Partial<HomeENDemoComposeTask>,
      };
    }
    case 'TICK': {
      // Batch-update entity fields for tick effects. A [runtime]-linked
      // trait's row lives in the `fields` mirror (transitions write it,
      // the view reads it), so updates ALWAYS merge there; the
      // `data` collection row is updated too when one exists.
      const tickFields = { ...state.fields, ...action.updates } as Partial<HomeENDemoComposeTask>;
      const arr = state.data[action.collection];
      const target = arr?.[0];
      if (!target) return { ...state, fields: tickFields };
      const updated = { ...target, ...action.updates } as EntityRow;
      return {
        ...state,
        data: { ...state.data, [action.collection]: [updated, ...arr.slice(1)] },
        fields: tickFields,
      };
    }
    default:
      return state;
  }
}

const initialState: HomeENDemoComposeShellInternalState = {
  machineState: 'composing',
  data: {},
  lastPayload: undefined,
  lastEvent: null,
  slotWriter: {},
  loading: false,
  pendingEvent: null,
  pendingFrom: null,
  error: null,
  fields: {},
};

// ============================================================================
// Hook
// ============================================================================

import type { NavItem } from '@app/shared';
export interface HomeENDemoComposeShellConfig {
  linkedEntity?: string;
  entityId?: string;
  routeParams?: Record<string, string | undefined>;
  initPayload?: EventPayload;
  accessDeniedDescription: string;
  accessDeniedTitle: string;
  allowedRoles: string[];
  appName?: string;
  contentClassName: string;
  contentTrait: string;
  currentPath: string;
  fullBleed: boolean;
  layoutMode: 'sidebar' | 'topnav' | 'bottomnav' | 'minimal' | 'drawer';
  layoutPattern: string;
  logoSrc: string;
  navItems: { href: string; icon: string; label: string[] }[];
  notificationClickEvent: string;
  notifications: { id?: string; message?: string; variant?: string }[];
  searchEvent: string;
  showNotifications: boolean;
  showSearch: boolean;
  showThemeToggle: boolean;
  sidebarTrait?: string;
  theme: string;
  topBarActions: { event?: string; icon?: string; label: string; navigatesTo?: string; roles?: string[]; variant?: string }[];
  userMenuItems: { event?: string; icon?: string; label: string; navigatesTo?: string; roles?: string[]; variant?: string }[];
  viewerAvatar: string;
  viewerEmail: string;
  viewerName: string;
  viewerRole: string;
}

const DEFAULT_HOME_EN_DEMO_COMPOSE_SHELL_CONFIG = {
  accessDeniedDescription: "Ask an administrator if you need it.",
  accessDeniedTitle: "You don't have access to this page",
  allowedRoles: [],
  contentClassName: "w-full",
  contentTrait: "@trait.HomeENDemoComposeContent",
  currentPath: "/demo/compose",
  fullBleed: false,
  layoutMode: "drawer",
  layoutPattern: "dashboard-layout",
  logoSrc: "",
  navItems: [{ href: "/demo/compose", icon: "layout-dashboard", label: ["i18n/t", "std-almadar-demo-compose:nav.overview"] }, { href: "/demo/compose", icon: "kanban", label: ["i18n/t", "std-almadar-demo-compose:nav.board"] }, { href: "/demo/compose", icon: "users", label: ["i18n/t", "std-almadar-demo-compose:nav.team"] }, { href: "/demo/compose", icon: "bar-chart-2", label: ["i18n/t", "std-almadar-demo-compose:nav.reports"] }],
  notificationClickEvent: "NOTIFY_CLICK",
  notifications: [],
  searchEvent: "SEARCH",
  showNotifications: true,
  showSearch: true,
  showThemeToggle: false,
  theme: "",
  topBarActions: [],
  userMenuItems: [],
  viewerAvatar: "",
  viewerEmail: "maya@copperline.example",
  viewerName: "Maya Hart",
  viewerRole: "",
} as HomeENDemoComposeShellConfig;

export interface HomeENDemoComposeShellLogicReturn {
  state: HomeENDemoComposeShellState;
  data: Record<string, EntityRow[]>;
  lastPayload: EventPayload | undefined;
  lastEvent: HomeENDemoComposeShellEvent | null;
  slotWriter: Partial<Record<HomeENDemoComposeShellSlot, HomeENDemoComposeShellEvent | null>>;
  loading: boolean;
  pendingEvent: HomeENDemoComposeShellEvent | null;
  pendingFrom: string | null;
  error: string | null;
  fields: Partial<HomeENDemoComposeTask>;
  dispatch: (event: string, payload?: EventPayload) => Promise<EventResponse>;
  actions: { [K in keyof HomeENDemoComposeShellEventPayloadMap]: (payload?: HomeENDemoComposeShellEventPayloadMap[K]) => Promise<EventResponse> };
  config: HomeENDemoComposeShellConfig;
}

const __LEG_EVENTS: ReadonlySet<string> = new Set(['INIT']);

export function useHomeENDemoComposeShellLogic(config?: Partial<HomeENDemoComposeShellConfig>): HomeENDemoComposeShellLogicReturn {
  const mergedConfig: HomeENDemoComposeShellConfig = { ...DEFAULT_HOME_EN_DEMO_COMPOSE_SHELL_CONFIG, ...(config ?? {}) };

  // Orbital bridge: event bus + server communication
  const { sendEvent, eventBus } = useOrbitalBridge({ basePath: '/api/home-en-demo-compose-shell', orbitalName: 'HomeEN', traitName: 'HomeENDemoComposeShell' });

  // Internal state with reducer
  const [internalState, dispatchReducer] = useReducer(homeENDemoComposeShellReducer, initialState);
  const { machineState: state, data, lastPayload, lastEvent, slotWriter, loading, pendingEvent, pendingFrom, error, fields: _initialFields } = internalState;
  const fields = _initialFields;
  const internalStateRef = useRef(internalState);
  internalStateRef.current = internalState;

  const machineStateRef = useRef(state);
  machineStateRef.current = state;

  const fieldsRef = useRef<EntityRow>({ ...fields });
  fieldsRef.current = { ...fields };
  const commandPump = useMemo(() => createCommandSendPump(), []);

  // Actor model: sequential event queue (prevents cross-trait race conditions)
  const eventQueueRef = useRef<Array<{ event: string; payload?: EventPayload; settle?: () => void }>>([]);
  const processingRef = useRef(false);

  const { t: __t, locale: __locale } = useTranslate();
  // Transpiled client-side effects (Phase 4)
  // Each (fromState, event) transition's client effects are compiled to TypeScript.
  const executeTransitionEffects = useCallback((fromState: string, event: string, liveData: Record<string, EntityRow[]>, _payload?: EventPayload): { fields: Record<string, EventPayloadValue | undefined>; newState: string | undefined; emits: Array<() => void> } => {
    const _fields: Record<string, EventPayloadValue | undefined> = {};
    const _emits: Array<() => void> = [];
    let _newState: string | undefined = undefined;
    const entity: EntityRow[] = liveData['HomeENDemoComposeTask'] ?? [];
    let fields: Record<string, EventPayloadValue | undefined> = entity[0] ?? {};

    if (fromState === 'composing' && event === 'INIT' && (((([]).length === 0) || (([]) as readonly EventPayloadValue[]).includes("" as EventPayloadValue)))) {
      // composing → composing (on INIT)
      _newState = 'composing';
    } else if (fromState === 'composing' && event === 'INIT' && (!((([]).length === 0) || (([]) as readonly EventPayloadValue[]).includes("" as EventPayloadValue)))) {
      // composing → composing (on INIT)
      _newState = 'composing';
    } else if (fromState === 'composing' && event === 'AppLayoutStateLoaded') {
      // composing → composing (on AppLayoutStateLoaded)
      _newState = 'composing';
    } else if (fromState === 'composing' && event === 'AppLayoutStateLoadFailed') {
      // composing → composing (on AppLayoutStateLoadFailed)
      _newState = 'composing';
    } else if (fromState === 'composing' && event === 'SEARCH') {
      // composing → composing (on SEARCH)
      _newState = 'composing';
    } else if (fromState === 'composing' && event === 'NOTIFY_CLICK') {
      // composing → composing (on NOTIFY_CLICK)
      _newState = 'composing';
    }
    return { fields: _fields, newState: _newState, emits: _emits };
  }, [eventBus]);

  // Per-transition render-ui presence map (codegen).
  // Used by dispatch to skip lastPayload/lastEvent updates for
  // transitions whose only effects are server-side or set/emit —
  // those don't change the rendered JSX, so the view should keep
  // the prior frame's payload until a render-bearing transition
  // (e.g. fetch.emit.success cascade) lands.
  const getRenderUiSlots = useCallback((fromState: string, event: string): { write: HomeENDemoComposeShellSlot[]; clear: HomeENDemoComposeShellSlot[] } => {
    switch (fromState) {
      case 'composing':
        switch (event) {
          case 'AppLayoutStateLoadFailed': return { write: ['toast'] as HomeENDemoComposeShellSlot[], clear: [] as HomeENDemoComposeShellSlot[] };
          case 'INIT': return { write: ['main'] as HomeENDemoComposeShellSlot[], clear: [] as HomeENDemoComposeShellSlot[] };
          default: return { write: [], clear: [] };
        }
      default:
        return { write: [], clear: [] };
    }
  }, []);

  /**
   * Dispatch an event through the orbital bridge.
   * Bridge emits lifecycle events on the bus, forwards to server.
   * Client effects are transpiled at compile time (Phase 4).
   */
  const dispatch = useCallback(async (event: string, __rawPayload?: EventPayload): Promise<EventResponse> => {
    const payload: EventPayload | undefined =
      __rawPayload !== undefined && ('entityId' in __rawPayload || 'id' in __rawPayload)
        ? __rawPayload
        : config?.entityId !== undefined
          ? { ...(__rawPayload ?? {}), entityId: config.entityId }
          : __rawPayload;
    const prevState = machineStateRef.current;
    // T9 optimistic dispatch: the fields mirror IS the [runtime] entity
    // row, so guard + to-state resolve locally on the same data the
    // server would read. Commit first, POST after; reconcile below.
    const liveData: Record<string, EntityRow[]> = { 'HomeENDemoComposeTask': [fieldsRef.current as EntityRow] };
    const _optimistic = executeTransitionEffects(prevState, event, liveData, payload);
    if (_optimistic.newState === undefined) {
      // Guard rejected or no transition defined — mirrors the server's
      // rejection, without the round trip.
      const errorMsg = `No transition defined for ${event} from ${prevState} (or guard rejected)`;
      dispatchReducer({ type: 'EVENT_ERROR', error: errorMsg });
      return { success: false, transitioned: false, states: {}, emittedEvents: [], error: errorMsg } as EventResponse;
    }
    const _renderSlots = getRenderUiSlots(prevState, event);
    const _renderBearing = _renderSlots.write.length > 0 || _renderSlots.clear.length > 0;
    // Snapshot the touched fields' prior values for a reconcile rollback.
    const _snapshot: Record<string, EventPayloadValue | undefined> = {};
    for (const _k of Object.keys(_optimistic.fields)) { _snapshot[_k] = fieldsRef.current[_k]; }
    dispatchReducer({
      type: 'EVENT_SUCCESS',
      event: event as HomeENDemoComposeShellEvent,
      newState: _optimistic.newState,
      data: {},
      payload,
      fields: _optimistic.fields,
      renderBearing: _renderBearing,
      renderWrite: _renderSlots.write,
      renderClear: _renderSlots.clear,
    });
    machineStateRef.current = _optimistic.newState as typeof machineStateRef.current;
    fieldsRef.current = { ...fieldsRef.current, ..._optimistic.fields } as EntityRow;
    for (const _emit of _optimistic.emits) _emit();

    if (__LEG_EVENTS.has(event)) {
    // Server leg: ordered by the pump, never blocking the drain.
    commandPump.enqueue(async () => {
      try {
        const response = await sendEvent(event, payload, prevState);
        if (response.success) {
          // Agreement — merge any fetch/persist results the local commit
          // couldn't supply (same reducer action, deferred leg).
          if (response.data && Object.keys(response.data).length > 0) {
            dispatchReducer({
              type: 'EVENT_SUCCESS',
              event: event as HomeENDemoComposeShellEvent,
              newState: machineStateRef.current,
              data: response.data,
              renderBearing: false,
            });
          }
        } else {
          // Rejection or state disagreement — roll back to the
          // pre-dispatch snapshot; the server stays authoritative.
          dispatchReducer({
            type: 'EVENT_SUCCESS',
            event: event as HomeENDemoComposeShellEvent,
            newState: prevState,
            data: {},
            fields: _snapshot,
            renderBearing: false,
          });
          machineStateRef.current = prevState;
          fieldsRef.current = { ...fieldsRef.current, ..._snapshot } as EntityRow;
          dispatchReducer({ type: 'EVENT_ERROR', error: response.error ?? `server state mismatch: expected ${_optimistic.newState}, got ${response.states?.['HomeENDemoComposeShell'] ?? '(unchanged)'}` });
        }
      } catch (err) {
        // Pump isolates the rejection; surface it without rolling
        // back — local state may simply be ahead of a reconnecting server.
        dispatchReducer({ type: 'EVENT_ERROR', error: err instanceof Error ? err.message : 'Network error' });
      }
    });
    }
    return { success: true, transitioned: true, states: { 'HomeENDemoComposeShell': _optimistic.newState }, emittedEvents: [] } as EventResponse;
  }, [sendEvent, executeTransitionEffects, commandPump, config?.entityId]);

  // Actor model: drain event queue one at a time
  const drainQueue = useCallback(async () => {
    if (processingRef.current) return;
    processingRef.current = true;
    try {
      while (eventQueueRef.current.length > 0) {
        const entry = eventQueueRef.current.shift()!;
        try { await dispatch(entry.event, entry.payload); } finally { entry.settle?.(); }
      }
    } finally {
      processingRef.current = false;
    }
  }, [dispatch]);

  // Actor model: enqueue event and start drain
  const enqueueEvent = useCallback((event: string, payload?: EventPayload) => {
    return new Promise<void>((settle) => {
    eventQueueRef.current.push({ event, payload, settle });
    void drainQueue();
    });
  }, [drainQueue]);

  // Action creators (typed per-event)
  const actions: { [K in keyof HomeENDemoComposeShellEventPayloadMap]: (payload?: HomeENDemoComposeShellEventPayloadMap[K]) => Promise<EventResponse> } = {
    'INIT': () => dispatch('INIT'),
    'AppLayoutStateLoaded': (payload?: HomeENDemoComposeShellEventPayloadMap['AppLayoutStateLoaded']) => dispatch('AppLayoutStateLoaded', payload),
    'AppLayoutStateLoadFailed': (payload?: HomeENDemoComposeShellEventPayloadMap['AppLayoutStateLoadFailed']) => dispatch('AppLayoutStateLoadFailed', payload),
    'SEARCH': (payload?: HomeENDemoComposeShellEventPayloadMap['SEARCH']) => dispatch('SEARCH', payload),
    'NOTIFY_CLICK': (payload?: HomeENDemoComposeShellEventPayloadMap['NOTIFY_CLICK']) => dispatch('NOTIFY_CLICK', payload),
  };

  useUIEvents(enqueueEvent, 'HomeEN.HomeENDemoComposeShell', ['INIT', 'AppLayoutStateLoaded', 'AppLayoutStateLoadFailed', 'SEARCH', 'NOTIFY_CLICK'] as const, eventBus);

  // Verifier snapshot registration (VG Foundation 1)
  useEffect(() => {
    const unregister = registerTraitSnapshot('HomeENDemoComposeShell', (): TraitStateSnapshot => ({
      traitName: 'HomeENDemoComposeShell',
      currentState: internalStateRef.current.machineState,
      states: ['composing'],
      events: ['INIT', 'AppLayoutStateLoaded', 'AppLayoutStateLoadFailed', 'SEARCH', 'NOTIFY_CLICK'],
      data: internalStateRef.current.data,
      lastPayload: internalStateRef.current.lastPayload,
      lastEventDispatched: internalStateRef.current.lastEvent !== null
        ? {
            event: internalStateRef.current.lastEvent,
            ...(internalStateRef.current.lastPayload !== undefined
              ? { payload: internalStateRef.current.lastPayload }
              : {}),
            timestamp: Date.now(),
          }
        : undefined,
      cascadeReceived: [],
    }));
    return unregister;
  // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  // Auto-init on mount
  useEffect(() => {
    const __rp = Object.fromEntries(Object.entries(config?.routeParams ?? {}).filter(([, v]) => v !== undefined));
    const __init = { ...__rp, ...(config?.initPayload ?? {}), ...(config?.entityId !== undefined ? { entityId: config.entityId } : {}) };
    dispatch('INIT', Object.keys(__init).length > 0 ? __init : undefined);
  // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  mergedConfig.appName = mergedConfig.appName ?? (__t("std-almadar-demo-compose:app.name"));
  return {
    state,
    data,
    lastPayload,
    lastEvent,
    slotWriter,
    loading,
    pendingEvent,
    pendingFrom,
    error,
    fields,
    dispatch,
    actions,
    config: mergedConfig,
  };
}
